import {
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import type { Opportunity } from "@/hooks/api/use-account-opportunities";
import { getTimestamp } from "@/lib/date-formatting";
import { OpportunityTooltip } from "./chart-tooltips.tsx";
import {
  getOutcomeCategory,
  type OpportunityDataPoint,
  type VisibleTimeRange,
} from "./formatters.ts";
import { useOpportunityChartData } from "./hooks";
import { getStageColor } from "./utils";

// Define types for opportunity and transition
interface StageTransition {
  stage: string;
  timestamp: number;
}

// Extended data point interface to include outcome information
interface EnhancedOpportunityDataPoint extends OpportunityDataPoint {
  outcomeOpportunities?: Array<{
    id: string;
    name: string;
    stage: string;
    outcome: "won" | "lost" | "active";
    timestamp: number;
    y: number;
    stageTransitions?: Array<StageTransition>;
  }>;
}

interface OpportunityChartProps {
  accountId?: string;
  domain: [number, number];
  ticks: number[];
  visibleRange?: { start: string; end: string };
  visibleTimeRange: VisibleTimeRange | null;
  isLoading?: boolean;
  onDateClick?: (date: string) => void;
  opportunitiesOverride?: Opportunity[];
  /** When true, renders date labels on the X-axis (useful when this is the bottom-most chart) */
  showDateLabels?: boolean;
  /** Formatter function for date tick labels (required when showDateLabels is true) */
  tickFormatter?: (value: number) => string;
}

// Type for the event object passed by Recharts mouse handlers
interface ChartMouseEvent {
  activeLabel?: string | number; // Timestamp of the hovered point
  activePayload?: Array<{
    // Data associated with hovered elements (lines, dots)
    payload?: EnhancedOpportunityDataPoint; // Original data point for this chart
    name?: string; // dataKey name (e.g., opportunity ID or "opportunity_outcome")
    dataKey?: string;
    value?: number; // y-value of the specific line/dot hovered
  }>;
  chartX?: number; // Mouse X relative to chart container
  chartY?: number; // Mouse Y relative to chart container
}

export function OpportunityChart({
  accountId,
  domain,
  ticks,
  visibleRange,
  visibleTimeRange,
  isLoading = false,
  onDateClick,
  opportunitiesOverride,
  showDateLabels = false,
  tickFormatter,
}: OpportunityChartProps) {
  const { opportunityData, processStageTransitions, opportunities } =
    useOpportunityChartData(visibleTimeRange, accountId, opportunitiesOverride);

  // Define handlers before potential early return
  const handleChartClick = (data: ChartMouseEvent): void => {
    if (data?.activeLabel && onDateClick) {
      try {
        const timestamp =
          typeof data.activeLabel === "string"
            ? parseInt(data.activeLabel, 10)
            : data.activeLabel;

        if (Number.isFinite(timestamp) && timestamp > 0) {
          onDateClick(new Date(timestamp).toISOString());
        }
      } catch (error) {
        console.error(
          "Invalid date value in chart click:",
          data.activeLabel,
          error,
        );
      }
    }
  };

  // Early return if loading
  if (isLoading) {
    return <Skeleton className="w-full h-full" />;
  }

  // Process data after loading check
  const enhancedData = opportunityData as EnhancedOpportunityDataPoint[];

  return (
    <div className="relative w-full h-full">
      {/* Style for circle hover effects */}
      <style>
        {`
          .outcome-dot circle {
            transition: r 0.15s ease, stroke-width 0.15s ease;
          }
          .outcome-dot:hover circle {
            r: 4;
            stroke-width: 2;
          }
        `}
      </style>

      <div className="relative w-full h-full overflow-visible">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={enhancedData}
            margin={{ top: 0, right: 20, left: 25, bottom: 0 }}
            onClick={handleChartClick}
          >
            <XAxis
              dataKey="timestamp"
              scale="time"
              type="number"
              domain={domain}
              ticks={ticks}
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={
                showDateLabels && tickFormatter
                  ? ({ x, y, payload }) => (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={0}
                          y={10}
                          textAnchor="middle"
                          fill="hsl(var(--muted-foreground))"
                          fontSize={12}
                        >
                          {tickFormatter(payload.value)}
                        </text>
                      </g>
                    )
                  : false
              }
              height={showDateLabels ? 30 : 0}
              pointerEvents="none"
            />
            <YAxis yAxisId="opportunities" hide domain={[-0.1, 1]} />
            <Tooltip
              content={<OpportunityTooltip />}
              wrapperStyle={{ position: "absolute", zIndex: 50 }}
              cursor={{ stroke: "hsl(var(--chart-5))", strokeWidth: 1 }}
              position={{ y: 0 }}
              isAnimationActive={false}
            />
            <Line
              yAxisId="opportunities"
              type="linear"
              dataKey={(dataPoint: EnhancedOpportunityDataPoint) => {
                if (dataPoint.outcomeOpportunities?.length) {
                  return dataPoint.outcomeOpportunities[0].y;
                }
                return null;
              }}
              name="opportunity_outcome"
              stroke="transparent"
              fill="transparent"
              activeDot={true}
              dot={false}
              isAnimationActive={false}
            />
            {(() => {
              if (!opportunities?.length) return null;

              // Create a map for faster lookups instead of using find() repeatedly
              const opportunityMap = new Map<
                string,
                {
                  id: string;
                  name: string;
                  outcome: "won" | "lost" | "active";
                  stageTransitions: Array<{
                    stage: string;
                    timestamp: number;
                  }>;
                  createdDate: string;
                  closeDate: string | null;
                }
              >();

              // Process all opportunities once
              opportunities.forEach((opp) => {
                opportunityMap.set(opp.id, {
                  id: opp.id,
                  name: opp.name,
                  outcome: getOutcomeCategory(opp.is_closed, opp.is_won),
                  stageTransitions: processStageTransitions(opp),
                  createdDate: opp.created_date,
                  closeDate: opp.close_date,
                });
              });

              // Create a map of opportunity positions for faster lookups
              const opportunityPositionMap = new Map<string, number>();
              enhancedData.forEach((dataPoint) => {
                dataPoint.opportunities?.forEach((opp) => {
                  if (!opportunityPositionMap.has(opp.id)) {
                    opportunityPositionMap.set(opp.id, opp.y);
                  }
                });
              });

              // Process only opportunities that appear in the chart data
              const outcomeMarkers: React.ReactNode[] = [];
              const lineSegments: React.ReactNode[] = [];

              // Single iteration to collect unique opportunities and process them
              opportunityPositionMap.forEach((yPosition, oppId) => {
                const oppData = opportunityMap.get(oppId);
                if (!oppData) return;

                // For the end time, use close date if available, otherwise use the domain end
                const oppEndTime = oppData.closeDate
                  ? getTimestamp(oppData.closeDate)
                  : domain[1];

                // Only add outcome marker for closed opportunities with a clear outcome
                if (oppData.closeDate && oppData.outcome !== "active") {
                  const outcomeColor =
                    oppData.outcome === "won"
                      ? "hsl(var(--chart-4))" // Supporting Green
                      : "hsl(var(--chart-1))"; // Supporting Red

                  // Add a reference dot at the end of the line
                  outcomeMarkers.push(
                    <ReferenceDot
                      key={`outcome-${oppData.id}`}
                      yAxisId="opportunities"
                      x={getTimestamp(oppData.closeDate)}
                      y={yPosition}
                      r={3}
                      fill={outcomeColor}
                      stroke={outcomeColor}
                      strokeWidth={1}
                      ifOverflow="visible"
                      className="outcome-dot cursor-pointer"
                      pointerEvents="none"
                    />,
                  );
                }

                // For opportunities with multiple stage transitions, render multiple line segments
                oppData.stageTransitions.forEach(
                  (transition, stageIndex, allTransitions) => {
                    // Skip the last transition as it doesn't have a segment after it
                    if (stageIndex === allTransitions.length - 1) {
                      // For the last stage, create a segment from the last transition to the close date or current time
                      const lastTransitionTimestamp = transition.timestamp;
                      lineSegments.push(
                        <Line
                          key={`${oppData.id}-stage-${stageIndex}`}
                          yAxisId="opportunities"
                          type="stepAfter"
                          dataKey={(
                            dataPoint: EnhancedOpportunityDataPoint,
                          ) => {
                            const timestamp = dataPoint.timestamp;
                            // Show this segment from the last transition to the end time
                            if (
                              timestamp >= lastTransitionTimestamp &&
                              timestamp <= oppEndTime
                            ) {
                              return yPosition;
                            }
                            return null;
                          }}
                          stroke={getStageColor(
                            stageIndex,
                            allTransitions.length,
                            oppData.outcome,
                          )}
                          strokeWidth={3}
                          dot={false}
                          isAnimationActive={false}
                          connectNulls={true}
                          name={oppData.name}
                        />,
                      );
                      return;
                    }

                    const nextTransition = allTransitions[stageIndex + 1];
                    const segmentStartTimestamp = transition.timestamp;
                    const segmentEndTimestamp = nextTransition.timestamp;

                    lineSegments.push(
                      <Line
                        key={`${oppData.id}-stage-${stageIndex}`}
                        yAxisId="opportunities"
                        type="stepAfter"
                        dataKey={(dataPoint: EnhancedOpportunityDataPoint) => {
                          const timestamp = dataPoint.timestamp;
                          // Only show this segment between its start and end times
                          if (
                            timestamp >= segmentStartTimestamp &&
                            timestamp < segmentEndTimestamp
                          ) {
                            return yPosition;
                          }
                          return null;
                        }}
                        stroke={getStageColor(
                          stageIndex,
                          allTransitions.length,
                          oppData.outcome,
                        )}
                        strokeWidth={3}
                        dot={false}
                        isAnimationActive={false}
                        connectNulls={true}
                        name={oppData.name}
                      />,
                    );
                  },
                );
              });

              // Return both line segments and outcome markers
              return [...lineSegments, ...outcomeMarkers];
            })()}

            {/* Add visible range indicators */}
            {visibleRange && (
              <>
                <ReferenceLine
                  yAxisId="opportunities"
                  x={Math.max(
                    domain[0],
                    new Date(visibleRange.start).getTime(),
                  )}
                  stroke="hsl(var(--chart-5))"
                  strokeWidth={0.5}
                  className="transition-all duration-200 ease-out"
                  pointerEvents="none"
                />
                <ReferenceLine
                  yAxisId="opportunities"
                  x={Math.min(domain[1], new Date(visibleRange.end).getTime())}
                  stroke="hsl(var(--chart-5))"
                  strokeWidth={0.5}
                  className="transition-all duration-200 ease-out"
                  pointerEvents="none"
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
